import React, { FC, useEffect, useState, useCallback, useMemo } from 'react';
import { FlexBox, useToasts, useIsMobile } from '@brg/ui';
import styled, { useTheme } from 'styled-components';
import { API, LikeAPI, LikeEntity, LikeObject, QualityAPI, LikeStatisticsAPI, Designer, Endpoints } from '@brg/api';
import { useProfile, useFingerprintId } from '@brg/auth';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { CurrentProjectActions, CurrentProjectSelector } from "shared/reducers/CurrentProject";
import { HsePolicies } from 'shared/constants/HsePolicies';
import { IconWrapperSpecial } from 'shared/assets/styles';
import { HintTooltip } from '@brg/ui';
import { getTooltipStyles, tooptipStylesWhite } from 'shared/constants/tooptipStyles';
import { AppTester } from 'supporting';
import LikesIcon32Fill from 'shared/assets/32/LikesIcon32Fill';
import LikesIcon32Empty from 'shared/assets/32/LikesIcon32Empty';
import { FInter } from '@brg/ui';
import { ProjectDesignersTooltip } from 'core/modules/likes.module';
import { useHistory } from 'react-router-dom';
import { LayoutConst } from 'core';

const TeamLikesWrapper = styled(FlexBox)`
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const TeamLikesCountText = styled(FInter.SmallText)`
  font-size: 17px;
  line-height: 22px;
  color: ${(p) => p.theme.icon.default};
  font-weight: 400;
  margin-left: 8px;

  @media (max-width: ${LayoutConst.screen.m}px) {
    font-size: 15px;
    line-height: 20px;
  }
`;

interface TeamLikesControllerProps extends LikeEntity {
  inActiveState?: boolean;
}

export const TeamLikesController: FC<TeamLikesControllerProps> = (props) => {
  const { t } = useTranslation();
  const { entityId, entityType, inActiveState } = props;
  const toasts = useToasts();
  const dispatch = useDispatch();
  const profile = useProfile();
  const fingerprintId = useFingerprintId();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useIsMobile();

  const [isLoading, setIsLoading] = useState(false);

  const isLiked = useSelector(CurrentProjectSelector)?.likes?.[entityId]?.isLiked;
  const likes = useSelector(CurrentProjectSelector)?.likes?.[entityId]?.likes;
  const anonymousLikes = useSelector(CurrentProjectSelector)?.likes?.[entityId]?.anonymousLikes;
  const items = useSelector(CurrentProjectSelector)?.likes?.[entityId]?.items || [];
  const itemsLikes = useSelector(CurrentProjectSelector)?.likes?.[entityId]?.itemsLikes || [];
  const paging = useSelector(CurrentProjectSelector)?.likes?.[entityId]?.itemsLikesPaging;
  const likeWeightByEntityId = useSelector(CurrentProjectSelector)?.likes?.[entityId]?.likeWeight || 1;

  const hasLikeWeightPolicy = useMemo(() => profile && profile.rolesList && Array.isArray(profile.rolesList)
    && profile.rolesList.some(x => x.startsWith(HsePolicies.like_value_prefix))
    , [profile]);

  const getLikeWeightFromPolicy = () => {
    if (!profile || !profile.rolesList || !hasLikeWeightPolicy) {
      return 1;
    }

    const policyLikeWeight = profile.rolesList
      .filter(x => x.startsWith(HsePolicies.like_value_prefix))
      .map(x => x.replace(HsePolicies.like_value_prefix, ''))
      .map(x => Number.isInteger(Number.parseInt(x)) ? Number.parseInt(x) : 0);
    return Math.max(1, ...policyLikeWeight);
  }

  const likeWeight = hasLikeWeightPolicy ? getLikeWeightFromPolicy() : likeWeightByEntityId;

  const setIsLiked = (id: string, value?: boolean) => {
    dispatch(CurrentProjectActions.setLikesField({ id, field: 'isLiked', value }))
  }

  const setLikes = (id: string, value: any) => {
    dispatch(CurrentProjectActions.setLikesField({ id, field: 'likes', value }))
  }

  const setAnonymousLikes = (id: string, value: any) => {
    dispatch(CurrentProjectActions.setLikesField({ id, field: 'anonymousLikes', value }))
  }

  const setItems = (id: string, value: Designer[]) => {
    dispatch(CurrentProjectActions.setLikesField({ id, field: 'items', value }))
  }

  const setItemsLikes = (id: string, value: any) => {
    dispatch(CurrentProjectActions.setLikesField({ id, field: 'itemsLikes', value }))
  }

  const setItemsLikesPaging = (id: string, value: any) => {
    dispatch(CurrentProjectActions.setLikesField({ id, field: 'itemsLikesPaging', value }))
  }

  const getDesignersByIds = useCallback((accountIds: string[], isAppend: boolean = false) => {
    if (!accountIds || accountIds.length === 0) {
      setItems(entityId, []);
      return;
    }

    API.request()
      .endpoint(Endpoints.geograffee)
      .url('/api/Designer/GetCollection')
      .get()
      .addQuery('idDesignerList', accountIds)
      .send()
      .then((b) => b.json())
      .then((b) => API.toLang<Designer[]>(b, i18next.language))
      .then((b: Designer[]) => {
        setItems(entityId, isAppend ? [...items, ...b] : b);
      })
      .catch((reason: any) => {
        console.error(reason);
      });
  }, [entityId, items, setItems]);

  const GetAccountIdsByPage = useCallback((currentPage: number) => {
    LikeAPI.GetAccountIdsOfEntityLikes(
      { entityId: entityId, entityType: entityType, limit: 50, page: currentPage })
      .then((v: any) => v.json())
      .then((v: any) => {
        setItemsLikes(entityId, currentPage > 1 ? [...itemsLikes, ...(v?.data || [])] : v?.data || []);
        if (v?.paging) setItemsLikesPaging(entityId, v?.paging);
        getDesignersByIds(v?.data?.map((item: any) => item?.accountId) || [], currentPage > 1);
        setIsLoading(false);
      })
      .catch((reason) => {
        console.error(reason);
        setIsLoading(false);
      });
  }, [entityId, entityType, itemsLikes, getDesignersByIds, setItemsLikes, setItemsLikesPaging, setIsLoading]);

  const GetMoreAccountIds = useCallback(() => {
    GetAccountIdsByPage(paging?.page + 1);
  }, [GetAccountIdsByPage, paging]);

  const GetLikeStatistics = useCallback(() => {
    setIsLoading(true);
    LikeStatisticsAPI.GetLikeStatistics(
      { entityId: entityId, entityType: entityType })
      .then((v: any) => v.json())
      .then((v: any) => {
        if (v?.userLike?.accountId) {
          setIsLiked(v?.entity?.entityId, v?.userLike?.isLike);
        }
        setLikes(v?.entity?.entityId, v?.count);
        setAnonymousLikes(v?.entity?.entityId, v?.notAuthLikesCount);
        setIsLoading(false);
      })
      .catch((reason) => {
        console.error(reason);
        setIsLoading(false);
      });
  }, [entityId, entityType, setIsLiked, setLikes, setAnonymousLikes, setIsLoading]);

  useEffect(() => {
    if (fingerprintId && entityType && entityId) {
      GetAccountIdsByPage(1);
      GetLikeStatistics();
    }
  }, [entityId, entityType, fingerprintId])

  const onAccountClick = useCallback((accountId: string) => {
    history.push(`/designer/${accountId}`);
  }, [history]);

  const onLikeClick = () => {
    if (isLoading || inActiveState) {
      return;
    }

    if (!profile?.accountId) {
      toasts.error(t('main_admin_tag.error.auth_error'));
      return;
    }

    setIsLiked(entityId, !Boolean(isLiked));
    setLikes(
      entityId,
      isLiked
        ? (likes || likeWeight) - likeWeight
        : (likes || 0) + likeWeight);

    if (!profile?.accountId) {
      setAnonymousLikes(
        entityId,
        isLiked
          ? (anonymousLikes || 1) - 1
          : (anonymousLikes || 0) + 1);
    }
    setIsLoading(true);
    LikeAPI.LikeV2({ entityId: props.entityId, entityType: props.entityType })
      .then((value: any) => value.json())
      .then((v: any) => {
        if (v === "Max limit") {
          toasts.error(t('like_limit_info'));
          setIsLiked(entityId, false)
          setLikes(entityId, (likes || 1))
          return "";
        }
        else {
          return API.toLang<LikeObject>(v, i18next.language)
        }
      })
      .then((v: any) => {
        if (v !== "") {
          GetLikeStatistics();
          GetAccountIdsByPage(1);
        }
        setIsLoading(false);
      })
      .catch((reason) => {
        console.error(reason);
        setIsLoading(false);
      });
  };

  const isGeo = AppTester.isGeograffee();
  const isSuperPuper = AppTester.isSuperPuper();
  const isGeoDafes = AppTester.isGeoDafes();
  const onWhiteBg = !isGeo && !isSuperPuper;

  const roundedLikes = useMemo(() => {
    const totalLikes = (likes || 0) + (anonymousLikes || 0);
    if (totalLikes >= 1000000) {
      return `${(totalLikes / 1000000).toFixed(1)}M`;
    } else if (totalLikes >= 1000) {
      return `${(totalLikes / 1000).toFixed(1)}K`;
    }
    return totalLikes.toString();
  }, [likes, anonymousLikes]);

  const hasMore = paging && paging.page < paging.pages;
  const totalLikes = (likes || 0) + (anonymousLikes || 0);
  const showPopup = totalLikes > 0 && items && items.length > 0 && !isMobile;

  return (
    <TeamLikesWrapper onClick={onLikeClick}>
      <FlexBox direction="row" align="center" className={'likes-icon-wrapper'}>
        {showPopup ? (
          <ProjectDesignersTooltip
            target={
              <IconWrapperSpecial disabled={inActiveState} pressed={isLiked}>
                <HintTooltip 
                  customStyle={isGeoDafes || onWhiteBg ? tooptipStylesWhite : getTooltipStyles(theme)} 
                  text={t("hints.like")} 
                  z={6} 
                  noDisappearDelay 
                  onWhiteBg={onWhiteBg}
                  positionY={'top'}
                  noArrow
                >
                  {isLiked ? (
                    <LikesIcon32Fill fill={'auto'} />
                  ) : (
                    <LikesIcon32Empty fill={'auto'} />
                  )}
                </HintTooltip>
              </IconWrapperSpecial>
            }
            hasMore={hasMore}
            getMore={GetMoreAccountIds}
            count={totalLikes}
            anonymousLikes={anonymousLikes || 0}
            items={items}
            itemsLikes={itemsLikes}
            onSelect={onAccountClick}
          />
        ) : (
          <IconWrapperSpecial disabled={inActiveState} pressed={isLiked}>
            <HintTooltip 
              customStyle={isGeoDafes || onWhiteBg ? tooptipStylesWhite : getTooltipStyles(theme)} 
              text={t("hints.like")} 
              z={6} 
              noDisappearDelay 
              onWhiteBg={onWhiteBg}
              positionY={'top'}
              noArrow
            >
              {isLiked ? (
                <LikesIcon32Fill fill={'auto'} />
              ) : (
                <LikesIcon32Empty fill={'auto'} />
              )}
            </HintTooltip>
          </IconWrapperSpecial>
        )}
        <TeamLikesCountText color={theme.likesController.text}>
          {roundedLikes}
        </TeamLikesCountText>
      </FlexBox>
    </TeamLikesWrapper>
  );
};
import React, { FC, useEffect, useState, useCallback, useMemo } from 'react';
import { FlexBox, useToasts, useIsMobile } from '@brg/ui';
import styled, { useTheme } from 'styled-components';
import { API, LikeAPI, LikeEntity, LikeObject, QualityAPI, LikeStatisticsAPI, Designer, Endpoints } from '@brg/api';
import { useProfile, useFingerprintId } from '@brg/auth';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { CurrentProjectActions, CurrentProjectSelector } from "shared/reducers/CurrentProject";
import { HsePolicies } from 'shared/constants/HsePolicies';
import { IconWrapperSpecial } from 'shared/assets/styles';
import { HintTooltip } from '@brg/ui';
import { getTooltipStyles, tooptipStylesWhite } from 'shared/constants/tooptipStyles';
import { AppTester } from 'supporting';
import LikesIcon32Fill from 'shared/assets/32/LikesIcon32Fill';
import LikesIcon32Empty from 'shared/assets/32/LikesIcon32Empty';
import { FInter } from '@brg/ui';
import { ProjectDesignersTooltip } from 'core/modules/likes.module';
import { useHistory } from 'react-router-dom';
import { LayoutConst } from 'core';

const TeamLikesWrapper = styled(FlexBox)`
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const TeamLikesCountText = styled(FInter.SmallText)`
  font-size: 17px;
  line-height: 22px;
  color: ${(p) => p.theme.icon.default};
  font-weight: 400;
  margin-left: 8px;

  @media (max-width: ${LayoutConst.screen.m}px) {
    font-size: 15px;
    line-height: 20px;
  }
`;

interface TeamLikesControllerProps extends LikeEntity {
  inActiveState?: boolean;
}

export const TeamLikesController: FC<TeamLikesControllerProps> = (props) => {
  const { t } = useTranslation();
  const { entityId, entityType, inActiveState } = props;
  const toasts = useToasts();
  const dispatch = useDispatch();
  const profile = useProfile();
  const fingerprintId = useFingerprintId();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useIsMobile();

  const [isLoading, setIsLoading] = useState(false);

  const isLiked = useSelector(CurrentProjectSelector)?.likes?.[entityId]?.isLiked;
  const likes = useSelector(CurrentProjectSelector)?.likes?.[entityId]?.likes;
  const anonymousLikes = useSelector(CurrentProjectSelector)?.likes?.[entityId]?.anonymousLikes;
  const items = useSelector(CurrentProjectSelector)?.likes?.[entityId]?.items || [];
  const itemsLikes = useSelector(CurrentProjectSelector)?.likes?.[entityId]?.itemsLikes || [];
  const paging = useSelector(CurrentProjectSelector)?.likes?.[entityId]?.itemsLikesPaging;
  const likeWeightByEntityId = useSelector(CurrentProjectSelector)?.likes?.[entityId]?.likeWeight || 1;

  const hasLikeWeightPolicy = useMemo(() => profile && profile.rolesList && Array.isArray(profile.rolesList)
    && profile.rolesList.some(x => x.startsWith(HsePolicies.like_value_prefix))
    , [profile]);

  const getLikeWeightFromPolicy = () => {
    if (!profile || !profile.rolesList || !hasLikeWeightPolicy) {
      return 1;
    }

    const policyLikeWeight = profile.rolesList
      .filter(x => x.startsWith(HsePolicies.like_value_prefix))
      .map(x => x.replace(HsePolicies.like_value_prefix, ''))
      .map(x => Number.isInteger(Number.parseInt(x)) ? Number.parseInt(x) : 0);
    return Math.max(1, ...policyLikeWeight);
  }

  const likeWeight = hasLikeWeightPolicy ? getLikeWeightFromPolicy() : likeWeightByEntityId;

  const setIsLiked = (id: string, value?: boolean) => {
    dispatch(CurrentProjectActions.setLikesField({ id, field: 'isLiked', value }))
  }

  const setLikes = (id: string, value: any) => {
    dispatch(CurrentProjectActions.setLikesField({ id, field: 'likes', value }))
  }

  const setAnonymousLikes = (id: string, value: any) => {
    dispatch(CurrentProjectActions.setLikesField({ id, field: 'anonymousLikes', value }))
  }

  const setItems = (id: string, value: Designer[]) => {
    dispatch(CurrentProjectActions.setLikesField({ id, field: 'items', value }))
  }

  const setItemsLikes = (id: string, value: any) => {
    dispatch(CurrentProjectActions.setLikesField({ id, field: 'itemsLikes', value }))
  }

  const setItemsLikesPaging = (id: string, value: any) => {
    dispatch(CurrentProjectActions.setLikesField({ id, field: 'itemsLikesPaging', value }))
  }

  const getDesignersByIds = useCallback((accountIds: string[], isAppend: boolean = false) => {
    if (!accountIds || accountIds.length === 0) {
      setItems(entityId, []);
      return;
    }

    API.request()
      .endpoint(Endpoints.geograffee)
      .url('/api/Designer/GetCollection')
      .get()
      .addQuery('idDesignerList', accountIds)
      .send()
      .then((b) => b.json())
      .then((b) => API.toLang<Designer[]>(b, i18next.language))
      .then((b: Designer[]) => {
        setItems(entityId, isAppend ? [...items, ...b] : b);
      })
      .catch((reason: any) => {
        console.error(reason);
      });
  }, [entityId, items, setItems]);

  const GetAccountIdsByPage = useCallback((currentPage: number) => {
    LikeAPI.GetAccountIdsOfEntityLikes(
      { entityId: entityId, entityType: entityType, limit: 50, page: currentPage })
      .then((v: any) => v.json())
      .then((v: any) => {
        setItemsLikes(entityId, currentPage > 1 ? [...itemsLikes, ...(v?.data || [])] : v?.data || []);
        if (v?.paging) setItemsLikesPaging(entityId, v?.paging);
        getDesignersByIds(v?.data?.map((item: any) => item?.accountId) || [], currentPage > 1);
        setIsLoading(false);
      })
      .catch((reason) => {
        console.error(reason);
        setIsLoading(false);
      });
  }, [entityId, entityType, itemsLikes, getDesignersByIds, setItemsLikes, setItemsLikesPaging, setIsLoading]);

  const GetMoreAccountIds = useCallback(() => {
    GetAccountIdsByPage(paging?.page + 1);
  }, [GetAccountIdsByPage, paging]);

  const GetLikeStatistics = useCallback(() => {
    setIsLoading(true);
    LikeStatisticsAPI.GetLikeStatistics(
      { entityId: entityId, entityType: entityType })
      .then((v: any) => v.json())
      .then((v: any) => {
        if (v?.userLike?.accountId) {
          setIsLiked(v?.entity?.entityId, v?.userLike?.isLike);
        }
        setLikes(v?.entity?.entityId, v?.count);
        setAnonymousLikes(v?.entity?.entityId, v?.notAuthLikesCount);
        setIsLoading(false);
      })
      .catch((reason) => {
        console.error(reason);
        setIsLoading(false);
      });
  }, [entityId, entityType, setIsLiked, setLikes, setAnonymousLikes, setIsLoading]);

  useEffect(() => {
    if (fingerprintId && entityType && entityId) {
      GetAccountIdsByPage(1);
      GetLikeStatistics();
    }
  }, [entityId, entityType, fingerprintId])

  const onAccountClick = useCallback((accountId: string) => {
    history.push(`/designer/${accountId}`);
  }, [history]);

  const onLikeClick = () => {
    if (isLoading || inActiveState) {
      return;
    }

    if (!profile?.accountId) {
      toasts.error(t('main_admin_tag.error.auth_error'));
      return;
    }

    setIsLiked(entityId, !Boolean(isLiked));
    setLikes(
      entityId,
      isLiked
        ? (likes || likeWeight) - likeWeight
        : (likes || 0) + likeWeight);

    if (!profile?.accountId) {
      setAnonymousLikes(
        entityId,
        isLiked
          ? (anonymousLikes || 1) - 1
          : (anonymousLikes || 0) + 1);
    }
    setIsLoading(true);
    LikeAPI.LikeV2({ entityId: props.entityId, entityType: props.entityType })
      .then((value: any) => value.json())
      .then((v: any) => {
        if (v === "Max limit") {
          toasts.error(t('like_limit_info'));
          setIsLiked(entityId, false)
          setLikes(entityId, (likes || 1))
          return "";
        }
        else {
          return API.toLang<LikeObject>(v, i18next.language)
        }
      })
      .then((v: any) => {
        if (v !== "") {
          GetLikeStatistics();
          GetAccountIdsByPage(1);
        }
        setIsLoading(false);
      })
      .catch((reason) => {
        console.error(reason);
        setIsLoading(false);
      });
  };

  const isGeo = AppTester.isGeograffee();
  const isSuperPuper = AppTester.isSuperPuper();
  const isGeoDafes = AppTester.isGeoDafes();
  const onWhiteBg = !isGeo && !isSuperPuper;

  const roundedLikes = useMemo(() => {
    const totalLikes = (likes || 0) + (anonymousLikes || 0);
    if (totalLikes >= 1000000) {
      return `${(totalLikes / 1000000).toFixed(1)}M`;
    } else if (totalLikes >= 1000) {
      return `${(totalLikes / 1000).toFixed(1)}K`;
    }
    return totalLikes.toString();
  }, [likes, anonymousLikes]);

  const hasMore = paging && paging.page < paging.pages;
  const totalLikes = (likes || 0) + (anonymousLikes || 0);
  const showPopup = totalLikes > 0 && items && items.length > 0 && !isMobile;

  return (
    <TeamLikesWrapper onClick={onLikeClick}>
      <FlexBox direction="row" align="center" className={'likes-icon-wrapper'}>
        {showPopup ? (
          <ProjectDesignersTooltip
            target={
              <IconWrapperSpecial disabled={inActiveState} pressed={isLiked}>
                <HintTooltip 
                  customStyle={isGeoDafes || onWhiteBg ? tooptipStylesWhite : getTooltipStyles(theme)} 
                  text={t("hints.like")} 
                  z={6} 
                  noDisappearDelay 
                  onWhiteBg={onWhiteBg}
                  positionY={'top'}
                  noArrow
                >
                  {isLiked ? (
                    <LikesIcon32Fill fill={'auto'} />
                  ) : (
                    <LikesIcon32Empty fill={'auto'} />
                  )}
                </HintTooltip>
              </IconWrapperSpecial>
            }
            hasMore={hasMore}
            getMore={GetMoreAccountIds}
            count={totalLikes}
            anonymousLikes={anonymousLikes || 0}
            items={items}
            itemsLikes={itemsLikes}
            onSelect={onAccountClick}
          />
        ) : (
          <IconWrapperSpecial disabled={inActiveState} pressed={isLiked}>
            <HintTooltip 
              customStyle={isGeoDafes || onWhiteBg ? tooptipStylesWhite : getTooltipStyles(theme)} 
              text={t("hints.like")} 
              z={6} 
              noDisappearDelay 
              onWhiteBg={onWhiteBg}
              positionY={'top'}
              noArrow
            >
              {isLiked ? (
                <LikesIcon32Fill fill={'auto'} />
              ) : (
                <LikesIcon32Empty fill={'auto'} />
              )}
            </HintTooltip>
          </IconWrapperSpecial>
        )}
        <TeamLikesCountText color={theme.likesController.text}>
          {roundedLikes}
        </TeamLikesCountText>
      </FlexBox>
    </TeamLikesWrapper>
  );
};
